import { LawyerConnection, MessageType, Prematter } from '@law-connect/types';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ContextMenu, formatDate } from '@law-connect/react-components';
import { File as AMFile } from '@law-connect/types';
import { Header } from '../header';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import selectors from '../../../redux/selectors';
import { usePrevious } from '../../../utils/use-previous';
import { actions } from '../../../redux/slices';
import style from './style.module.less';
import { ActionModal } from '../../../components/action-modal';
import { useRequestInfoDisabled } from '../../../utils/use-request-info-disabled';
import { FileIcon } from '../../../components/files/file-icon';

import EyeInvisibleIcon from '../eye-invisible.svg?react';
import DownloadIcon from './download.svg?react';
import FolderIcon from '../folder.svg?react';
import { Button, Tab, Tabs, Typography } from '@galaxy-ds/core';
import { steps } from '../../../components/onboarding/steps';
import {
  RequestPrivateInfoLink,
  RequestPrivateInfoModal,
  RequestPrivateInfoState,
} from '../../../components/request-modal';
import { getRequestedUserInfo } from '../../../utils/requested-user-info';
import { useTranslation } from 'react-i18next';

interface Props {
  connection: LawyerConnection;
  prematter: Prematter;
}

export const FileContainer: FC<Props> = (props) => {
  const { connection, prematter } = props;

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const files = useAppSelector(selectors.file.getFiles(connection.id));
  const isFetchingFiles = useAppSelector(selectors.file.isFetchingFiles());
  const prevIsFetchingFiles = usePrevious(isFetchingFiles);
  const fetchFilesError = useAppSelector(selectors.file.getFetchFilesError());
  const messages = useAppSelector(
    selectors.lawyerConnection.getMessagesById(connection.id)
  );

  const [isOpenRequestModal, setIsOpenRequestModal] = useState(false);

  const requestInfoDisabled = useRequestInfoDisabled({
    connection,
    messages,
  });
  const openRequestModal = useCallback(() => {
    !requestInfoDisabled && setIsOpenRequestModal(true);
  }, [requestInfoDisabled]);
  const closeRequestModal = useCallback(() => setIsOpenRequestModal(false), []);

  // we want to fetch the files for the message if we cant find the client file
  useEffect(() => {
    if (
      !files &&
      !isFetchingFiles &&
      !prevIsFetchingFiles &&
      !fetchFilesError
    ) {
      dispatch(actions.file.fetch({ connectionId: connection.id }));
    }
  }, [
    dispatch,
    fetchFilesError,
    files,
    isFetchingFiles,
    prevIsFetchingFiles,
    connection.id,
  ]);

  const onConfirmRequest = useCallback(() => {
    dispatch(
      actions.lawyerConnection.requestPermission({
        id: connection.id,
      })
    );
    closeRequestModal();
  }, [dispatch, connection?.id, closeRequestModal]);

  const downloadClick = useCallback((file: AMFile) => {
    if (file.url?.path) {
      document.location.href = file.url.path ?? '';
    }
  }, []);

  const isDisabled = useMemo(
    () => connection.endedAt !== null || connection.deletedAt !== null,
    [connection]
  );
  const requestedUserInfo = useMemo(
    () => !isDisabled && getRequestedUserInfo(messages, connection),
    [isDisabled, messages, connection]
  );

  const userInfoState = useMemo(() => {
    if (!connection) {
      return;
    }
    if (connection?.permissions?.userDetails) {
      // user permissions allowed
      return RequestPrivateInfoState.HIDE;
    } else if (requestedUserInfo) {
      return RequestPrivateInfoState.PENDING;
    } else {
      return RequestPrivateInfoState.REQUEST;
    }
  }, [connection, requestedUserInfo]);

  const Body = useMemo(() => {
    if (isFetchingFiles && !files?.prematter?.length && !files?.chat?.length) {
      return null;
    } else if (!connection.permissions?.prematterFiles) {
      return (
        <div className={style.noPermissionContainer}>
          <div className={style.row}>
            <div className={style.iconContainer}>
              <EyeInvisibleIcon className={style.icon} />
            </div>
            <div>{t('lawyer.files-hidden')}</div>
          </div>
          <RequestPrivateInfoLink
            openRequestModal={openRequestModal}
            state={userInfoState}
          />
        </div>
      );
    } else if (!files?.prematter?.length) {
      return (
        <div className={style.noPermissionContainer}>
          <div className={style.row}>
            <div className={style.iconContainer}>
              <FolderIcon className={style.icon} />
            </div>
            <div>{t('lawyer.no-files')}</div>
          </div>
        </div>
      );
    } else {
      return (
        <Tabs className={style.tabs} orientation={'vertical'} value={false}>
          {[...(files?.prematter ?? []), ...(files?.chat ?? [])]?.map(
            (file) => {
              const Body = (
                <div className={style.file}>
                  <FileIcon className={style.fileIcon} file={file} />
                  <div className={style.fileNameContainer}>
                    <Typography variant='body1' className={style.fileName}>
                      {file.name}
                    </Typography>
                    <Typography variant='body2' className={style.fileStatus}>
                      {formatDate(
                        file.createdAt,
                        prematter?.language
                      )}
                    </Typography>
                  </div>
                  <Button
                    variant='outlined'
                    onClick={() => downloadClick(file)}
                    className={style.downloadButton}
                  >
                    <DownloadIcon className={style.downloadIcon} />
                  </Button>
                </div>
              );
              return (
                <Tab key={file.id} className={style.fileTab} label={Body} />
              );
            }
          )}
        </Tabs>
      );
    }
  }, [
    isFetchingFiles,
    files?.prematter,
    connection.permissions?.prematterFiles,
    openRequestModal,
    userInfoState,
    downloadClick,
  ]);

  return (
    <>
      <Header
        Icon={FolderIcon}
        title={t('lawyer.files')}
        props={{
          'data-onboarding-id': steps[3].refId,
          'data-onboarding-position': 'right',
        }}
      />
      {Body}
      <RequestPrivateInfoModal
        isOpenRequestModal={isOpenRequestModal}
        closeRequestModal={closeRequestModal}
        onConfirmRequest={onConfirmRequest}
      />
    </>
  );
};
